import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Boat, AirplaneTilt, Truck, CaretRight, MagnifyingGlass, Package, ClockCountdown, CheckCircle, ArrowRight, EnvelopeSimple, Timer, CurrencyInr, FileText, Warning } from "@phosphor-icons/react";

const inrShort = (n) => {
  const v = Number(n || 0);
  if (v >= 1e7) return `₹${(v / 1e7).toFixed(2)}Cr`;
  if (v >= 1e5) return `₹${(v / 1e5).toFixed(2)}L`;
  return `₹${v.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
};
import { portalApi } from "@/lib/portalApi";
import { formatApiError } from "@/lib/api";
import { usePortalAuth } from "@/contexts/PortalAuthContext";
import { BRAND } from "./portalBrand";

const DAY = 86400000;
const dOnly = (s) => { if (!s) return null; const d = new Date(String(s).slice(0, 10)); return isNaN(d) ? null : d; };
const midnight = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; };

function ModeIcon({ mode, size = 15 }) {
  if (mode === "Air") return <AirplaneTilt size={size} />;
  if (mode === "Road") return <Truck size={size} />;
  return <Boat size={size} />;
}

const isDelivered = (s) => /deliver|closed|completed/i.test(s || "");
const isCancelled = (s) => /cancel/i.test(s || "");

function statusChip(s) {
  if (isDelivered(s)) return "text-emerald-700 border-emerald-200 bg-emerald-50";
  if (isCancelled(s)) return "text-slate-500 border-slate-200 bg-slate-100";
  return "text-blue-700 border-blue-200 bg-blue-50";
}

function voyage(j) {
  const today = midnight();
  const etd = dOnly(j.etd), eta = dOnly(j.eta);
  const arrived = isDelivered(j.status) || /discharg|arriv/i.test(j.status || "");
  let pct = 8;
  if (arrived) pct = 100;
  else if (etd && eta && eta > etd) pct = Math.max(5, Math.min(96, ((today - etd) / (eta - etd)) * 100));
  else if (/on vessel|sail|transit/i.test(j.status || "")) pct = 55;
  else if (/allot|booking|booked/i.test(j.status || "")) pct = 18;
  const days = eta ? Math.ceil((eta - today) / DAY) : null;
  let chip = null;
  if (arrived) chip = "Arrived";
  else if (days === null) chip = null;
  else if (days > 1) chip = `Arriving in ${days}d`;
  else if (days === 1) chip = "Arriving tomorrow";
  else if (days === 0) chip = "Arriving today";
  else chip = "ETA passed";
  return { pct, days, chip, arrived };
}

function Skeleton() {
  return (
    <div className="space-y-3">
      {[0, 1, 2].map((i) => (
        <div key={i} className="bg-white border border-slate-200 rounded-xl p-4 animate-pulse">
          <div className="h-3 w-40 bg-slate-200 rounded" />
          <div className="h-3 w-64 bg-slate-100 rounded mt-3" />
          <div className="h-2 w-full bg-slate-100 rounded mt-4" />
        </div>
      ))}
    </div>
  );
}

function Stat({ label, value, icon: Icon, tone }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3">
      <span className={`w-10 h-10 rounded-lg flex items-center justify-center ${tone}`}><Icon size={20} weight="bold" /></span>
      <div>
        <div className="text-2xl font-bold text-slate-900 leading-none">{value}</div>
        <div className="text-[11px] uppercase tracking-wider text-slate-500 mt-1">{label}</div>
      </div>
    </div>
  );
}

const TABS = ["Active", "Arriving", "Delivered", "All"];

export default function PortalShipments() {
  const { customer } = usePortalAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("Active");

  const [freeTime, setFreeTime] = useState([]);
  const [dash, setDash] = useState(null);

  useEffect(() => {
    portalApi.get("/portal/shipments").then((r) => setItems(r.data || [])).catch((e) => toast.error(formatApiError(e))).finally(() => setLoading(false));
    portalApi.get("/portal/free-time").then((r) => setFreeTime((r.data?.rows || []).filter((x) => x.days_left !== null && x.days_left <= 3))).catch(() => {});
    portalApi.get("/portal/dashboard").then((r) => setDash(r.data)).catch(() => {});
  }, []);

  const stats = useMemo(() => {
    const today = midnight();
    let active = 0, arriving = 0, delivered = 0;
    items.forEach((j) => {
      if (isDelivered(j.status)) delivered++;
      else if (!isCancelled(j.status)) {
        active++;
        const eta = dOnly(j.eta);
        if (eta && (eta - today) / DAY <= 7 && (eta - today) >= -DAY) arriving++;
      }
    });
    return { active, arriving, delivered };
  }, [items]);

  const nextArrival = useMemo(() => {
    const today = midnight();
    return items
      .filter((j) => !isDelivered(j.status) && !isCancelled(j.status) && dOnly(j.eta) && dOnly(j.eta) >= today)
      .sort((a, b) => dOnly(a.eta) - dOnly(b.eta))[0] || null;
  }, [items]);

  const filtered = useMemo(() => {
    const term = q.toLowerCase();
    return items.filter((j) => {
      if (tab === "Active" && (isDelivered(j.status) || isCancelled(j.status))) return false;
      if (tab === "Delivered" && !isDelivered(j.status)) return false;
      if (tab === "Arriving") { const v = voyage(j); if (v.arrived || v.days === null || v.days > 7 || v.days < -1) return false; }
      if (!term) return true;
      return [j.job_number, j.pol_name, j.pod_name, j.carrier_name, j.hbl_number, j.awb_number, j.vessel_name]
        .some((v) => (v || "").toLowerCase().includes(term));
    });
  }, [items, q, tab]);

  const firstName = (customer?.customer_name || "there").split(" ")[0];

  return (
    <div>
      {/* Welcome + account manager */}
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Welcome back, {firstName}</h1>
          <p className="text-slate-500 text-sm mt-1">Here's the latest on your shipments with {BRAND.name}.</p>
        </div>
        {customer?.account_manager?.name && (
          <div className="bg-white border border-slate-200 rounded-xl px-4 py-3 flex items-center gap-3 shadow-sm">
            <span className="w-10 h-10 rounded-full bg-blue-700 text-white flex items-center justify-center font-semibold text-sm">
              {customer.account_manager.name.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase()}
            </span>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400">Your account manager</div>
              <div className="text-sm font-semibold text-slate-800 leading-tight">{customer.account_manager.name}</div>
              {customer.account_manager.email && (
                <a href={`mailto:${customer.account_manager.email}`} className="text-[12px] text-blue-700 hover:underline inline-flex items-center gap-1"><EnvelopeSimple size={11} /> {customer.account_manager.email}</a>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <Stat label="In transit" value={dash ? dash.in_transit : stats.active} icon={Boat} tone="bg-blue-50 text-blue-700" />
        <Stat label="Arriving this week" value={dash ? dash.arriving_7d : stats.arriving} icon={ClockCountdown} tone="bg-amber-50 text-amber-600" />
        <Stat label="Outstanding balance" value={dash ? inrShort(dash.outstanding_inr) : "—"} icon={CurrencyInr} tone="bg-rose-50 text-rose-600" />
        <Stat label="Delivered" value={stats.delivered} icon={CheckCircle} tone="bg-emerald-50 text-emerald-600" />
      </div>

      {/* Action needed */}
      {dash && (dash.si_due > 0 || dash.bl_to_approve > 0 || dash.advance_inr > 0) && (
        <div className="grid sm:grid-cols-2 gap-3 mb-4">
          {dash.si_due > 0 && (
            <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 flex items-center gap-3">
              <span className="w-9 h-9 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center"><FileText size={18} weight="bold" /></span>
              <div className="text-sm text-blue-900"><span className="font-semibold">{dash.si_due}</span> export shipment{dash.si_due > 1 ? "s" : ""} awaiting your Shipping Instructions. <span className="text-blue-700">Open a shipment to submit.</span></div>
            </div>
          )}
          {dash.bl_to_approve > 0 && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 flex items-center gap-3">
              <span className="w-9 h-9 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center"><Warning size={18} weight="bold" /></span>
              <div className="text-sm text-amber-900"><span className="font-semibold">{dash.bl_to_approve}</span> draft B/L{dash.bl_to_approve > 1 ? "s" : ""} awaiting your approval.</div>
            </div>
          )}
          {dash.advance_inr > 0 && (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex items-center gap-3">
              <span className="w-9 h-9 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center"><CurrencyInr size={18} weight="bold" /></span>
              <div className="text-sm text-emerald-900">You have <span className="font-semibold">{inrShort(dash.advance_inr)}</span> advance / credit on account.</div>
            </div>
          )}
        </div>
      )}

      {/* Free-time / demurrage alert */}
      {freeTime.length > 0 && (
        <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-4">
          <div className="flex items-center gap-2 text-amber-800 font-semibold text-sm"><Timer size={16} weight="bold" /> Free-time ending soon on {freeTime.length} shipment{freeTime.length > 1 ? "s" : ""}</div>
          <div className="mt-2 space-y-1">
            {freeTime.slice(0, 4).map((f) => (
              <Link key={f.id} to={`/portal/shipments/${f.id}`} className="flex items-center justify-between text-sm text-amber-900 hover:underline">
                <span className="font-mono">{f.job_number}</span>
                <span>{f.pod_name || ""} · {f.days_left < 0 ? `${Math.abs(f.days_left)}d overdue` : f.days_left === 0 ? "last free day today" : `${f.days_left}d left`}</span>
              </Link>
            ))}
          </div>
          <div className="text-[11px] text-amber-700 mt-2">Arrange pickup to avoid demurrage / detention charges. Need help? Use the chat below.</div>
        </div>
      )}

      {/* Next arrival highlight */}
      {nextArrival && (() => {
        const v = voyage(nextArrival);
        return (
          <Link to={`/portal/shipments/${nextArrival.id}`} className="block mb-6 rounded-xl p-5 text-white relative overflow-hidden hover:brightness-110 transition"
            style={{ background: "linear-gradient(120deg,#1d4ed8,#2563eb)" }}>
            <div className="text-[11px] uppercase tracking-wider text-blue-100">Your next arrival</div>
            <div className="flex items-center justify-between gap-3 mt-1 flex-wrap">
              <div className="font-bold text-lg">{nextArrival.job_number} · {nextArrival.pol_name || "—"} <ArrowRight size={15} className="inline -mt-0.5" /> {nextArrival.pod_name || "—"}</div>
              <div className="text-blue-50 font-semibold inline-flex items-center gap-2"><ClockCountdown size={18} weight="bold" /> {v.chip || nextArrival.status}</div>
            </div>
            <div className="h-1.5 rounded-full bg-white/25 mt-3 overflow-hidden"><div className="h-full bg-white rounded-full" style={{ width: `${v.pct}%` }} /></div>
          </Link>
        );
      })()}

      {/* Tabs + search */}
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="inline-flex bg-white border border-slate-200 rounded-lg p-1">
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${tab === t ? "bg-blue-700 text-white" : "text-slate-600 hover:text-slate-900"}`}>{t}</button>
          ))}
        </div>
        <div className="relative w-full sm:w-72">
          <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search job, port, B/L, vessel…"
            className="w-full h-10 pl-9 pr-3 bg-white border border-slate-300 rounded-md text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" />
        </div>
      </div>

      {/* List */}
      {loading ? (
        <Skeleton />
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl py-16 text-center">
          <Package size={40} className="mx-auto text-slate-300" />
          <div className="text-slate-700 font-medium mt-3">No shipments {tab !== "All" ? `in "${tab}"` : "yet"}.</div>
          <div className="text-slate-500 text-sm mt-1">Your bookings will appear here. Need help? <a href={`mailto:${BRAND.supportEmail}`} className="text-blue-700 hover:underline">{BRAND.supportEmail}</a></div>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((j) => {
            const v = voyage(j);
            return (
              <Link key={j.id} to={`/portal/shipments/${j.id}`} data-testid={`portal-ship-${j.job_number}`}
                className="block bg-white border border-slate-200 rounded-xl p-4 hover:border-blue-300 hover:shadow-md hover:shadow-blue-50 transition-all">
                <div className="flex items-center justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono font-semibold text-slate-900 text-sm">{j.job_number}</span>
                      <span className={`text-[10px] font-semibold uppercase tracking-wider rounded-full px-2 py-0.5 border ${statusChip(j.status)}`}>{j.status}</span>
                      {v.chip && !v.arrived && <span className="text-[10px] font-semibold rounded-full px-2 py-0.5 bg-amber-50 text-amber-700 border border-amber-200">{v.chip}</span>}
                    </div>
                    <div className="flex items-center gap-2 text-slate-700 text-sm mt-2">
                      <span className="w-6 h-6 rounded bg-slate-100 text-slate-600 flex items-center justify-center shrink-0"><ModeIcon mode={j.mode} size={13} /></span>
                      <span className="truncate">{j.pol_name || "—"} <ArrowRight size={13} className="inline -mt-0.5 text-slate-400" /> {j.pod_name || "—"}</span>
                      {j.carrier_name ? <span className="text-slate-400 text-xs truncate">· {j.carrier_name}</span> : null}
                    </div>
                    <div className="text-[11px] text-slate-400 mt-1 font-mono">
                      {j.hbl_number || j.awb_number || ""}{(j.etd || j.eta) ? ` · ETD ${(j.etd || "—").slice(0, 10)} · ETA ${(j.eta || "—").slice(0, 10)}` : ""}
                    </div>
                    <div className="h-1.5 rounded-full bg-slate-100 mt-3 overflow-hidden max-w-md">
                      <div className="h-full rounded-full" style={{ width: `${v.pct}%`, background: v.arrived ? "#059669" : "linear-gradient(90deg,#2563eb,#059669)" }} />
                    </div>
                  </div>
                  <CaretRight size={18} className="text-slate-300 shrink-0" />
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
